export type GrowthEventName = 'landing_view' | 'example_open' | 'example_loaded' | 'register_click' | 'feedback_open' | 'feedback_submit';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface GrowthAttribution {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
}

export interface GrowthContext {
  sessionId: string;
  locale: string;
  utm: GrowthAttribution;
}

const PREFIX = 'kimap:growth:';
const SESSION_KEY = `${PREFIX}session`;
const ATTRIBUTION_KEY = `${PREFIX}attribution`;
const UTM_FIELDS = ['source', 'medium', 'campaign', 'content', 'term'] as const;
const IDEMPOTENT_EVENTS = new Set<GrowthEventName>(['landing_view', 'example_open', 'example_loaded']);

const browserStorage = (): StorageLike | undefined => typeof window === 'undefined' ? undefined : window.sessionStorage;
const bounded = (value: string | null) => value?.trim().slice(0, 100) || undefined;

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
};

export function captureGrowthAttribution(
  search = typeof window === 'undefined' ? '' : window.location.search,
  storage = browserStorage(),
): GrowthAttribution {
  if (!storage) return {};
  const existing = storage.getItem(ATTRIBUTION_KEY);
  if (existing) {
    try { return JSON.parse(existing) as GrowthAttribution; } catch { storage.removeItem(ATTRIBUTION_KEY); }
  }
  const params = new URLSearchParams(search);
  const attribution: GrowthAttribution = {};
  UTM_FIELDS.forEach(field => {
    const value = bounded(params.get(`utm_${field}`));
    if (value) attribution[field] = value;
  });
  storage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  return attribution;
}

export function getGrowthContext(
  storage = browserStorage(),
  locale = typeof navigator === 'undefined' ? 'en-US' : navigator.language,
): GrowthContext {
  if (!storage) return { sessionId: createSessionId(), locale, utm: {} };
  let sessionId = storage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = createSessionId();
    storage.setItem(SESSION_KEY, sessionId);
  }
  return { sessionId, locale, utm: captureGrowthAttribution(undefined, storage) };
}

export function withGrowthQuery(path: string, storage = browserStorage()): string {
  const attribution = getGrowthContext(storage).utm;
  const params = new URLSearchParams();
  UTM_FIELDS.forEach(field => {
    const value = attribution[field];
    if (value) params.set(`utm_${field}`, value);
  });
  const query = params.toString();
  return query ? `${path}${path.includes('?') ? '&' : '?'}${query}` : path;
}

export function trackGrowthEvent(name: GrowthEventName, page?: string): void {
  const storage = browserStorage();
  if (!storage || typeof fetch === 'undefined') return;
  const resolvedPage = page ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  const dedupeKey = `${PREFIX}sent:${name}:${resolvedPage}`;
  if (IDEMPOTENT_EVENTS.has(name) && storage.getItem(dedupeKey)) return;
  if (IDEMPOTENT_EVENTS.has(name)) storage.setItem(dedupeKey, '1');
  const context = getGrowthContext(storage);
  void fetch('/api/user/public/growth-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({ eventName: name, sessionId: context.sessionId, page: resolvedPage, locale: context.locale, utm: context.utm }),
  }).catch(error => {
    if (import.meta.env.DEV) console.debug('Growth event delivery failed', error);
  });
}
